import { app } from '@/main'
import { showError } from './axios'
import { usePrivilegesStore } from '@/store/privileges'
import { apiURL } from '@/utils/environment'

export class Privilege {
  path = '' // path without permission, without language, eg : /processingform, or /marketedMilks/findfarmermilk
  permission = '' // read | write | anything
}

class Privileges {
  private static _instance: Privileges

  public static get Instance() {
    return this._instance || (this._instance = new this())
  }

  static privilegesDefault: Privilege[] = [
    { path: '/home', permission: 'read' }
    // { path: '/profile', permission: 'read' }
  ]

  async loadPrivileges() {
    let privileges: Privilege[] | undefined = []
    const privilegesStore = usePrivilegesStore()
    try {
      privilegesStore.incLoading()
      privileges = await privilegesStore.fetchPrivileges()
    } catch (e) {
      showError(e)
    } finally {
      privilegesStore.decLoading()
    }
    return privileges
  }

  async loadLegacyApps() {
    try {
      const response = await app.axios.get(apiURL + '/privileges/legacyAccess')
      if (await response.data) {
        const data = await response.data
        const legacyApps = Object.keys(data).filter(function (app) {
          return data[app] === true
        })
        const privilegesStore = usePrivilegesStore()
        privilegesStore.setLegacyApps(legacyApps)
      }
    } catch (e) {
      showError(e)
    }
    return []
  }

  /** after a oidc silent login it's not nesecary to bring all privileges down. */
  async reload_silent() {
    try {
      return await privileges.loadPrivileges()
    } catch {
      privileges.reset()
    }
  }

  async reset_and_reload() {
    privileges.reset()
    return await privileges.loadPrivileges()
  }

  /** <b>Script</b>
   * <pre>
   * privileges.has({ path: '/messages/inbox', permission: 'read' })
   *</pre>
   * <b>Reactive</b>
   * <pre>
   * const canAccessMessageBox: ComputedRef<boolean> = computed(() =>
   *   privileges.has( { path: '/messages/inbox', permission: 'read' },
   *     usePrivilegesStore().privileges
   *   )
   * )
   * <pre>
   */
  has(
    requestedPrivilege: Privilege,
    loadedPrivileges: Privilege[] = usePrivilegesStore().privileges,
    defaultPrivileges: Privilege[] = Privileges.privilegesDefault
  ): boolean {
    if (loadedPrivileges.length == 0) {
      /* this helps to find */
      console.warn('no privileges loaded')
      return false
    }
    const allPrivileges: Privilege[] = typeof app != 'undefined' ? [...defaultPrivileges, ...loadedPrivileges] : defaultPrivileges
    return (
      allPrivileges.findIndex(
        (element) =>
          element.path.toLowerCase() == requestedPrivilege.path.toLowerCase() && element.permission.toLowerCase() == requestedPrivilege.permission.toLowerCase()
      ) != -1
    )
  }

  /** <b>Script</b>
   * <pre>
   * privileges.hasOneOf([
   *                 { path: '/messages/inbox', permission: 'read' },
   *                 { path: '/messages/outbox', permission: 'read' }
   *                 ])
   *</pre>
   * <b>Reactive</b>
   * <pre>
   * const canAccessMessageBox: ComputedRef<boolean> = computed(() =>
   *   privileges.hasOneOf(
   *     [
   *       { path: '/messages/inbox', permission: 'read' },
   *       { path: '/messages/outbox', permission: 'read' }
   *     ],
   *     usePrivilegesStore().privileges
   *   )
   * )
   * <pre>
   */
  hasOneOf(
    requestedPrivileges: Privilege[],
    loadedPrivileges: Privilege[] = usePrivilegesStore().privileges,
    defaultPrivileges: Privilege[] = Privileges.privilegesDefault
  ): boolean {
    let test = false
    requestedPrivileges.forEach((el) => {
      test = test || privileges.has(el, loadedPrivileges, defaultPrivileges)
    })
    return test
  }
  hasAllOf(
    requestedPrivileges: Privilege[],
    loadedPrivileges: Privilege[] = usePrivilegesStore().privileges,
    defaultPrivileges: Privilege[] = Privileges.privilegesDefault
  ): boolean {
    let test = true
    requestedPrivileges.forEach((el) => {
      test = test && privileges.has(el, loadedPrivileges, defaultPrivileges)
    })
    return test
  }

  reset() {
    const privilegesStore = usePrivilegesStore()
    privilegesStore.setPrivileges([])
    privilegesStore.setLegacyApps([])
  }
}

export const privileges = Privileges.Instance
